/**
 * `/poste/attendus` — what the desk should expect.
 *
 * The privacy rule is enforced by the API, not here (see
 * `packages/api/src/router/expected.ts`): today arrives detailed, the week and
 * the month arrive as anonymous counts. That is why this file reads two
 * different procedures rather than filtering one.
 *
 * Three renderings, because the data genuinely differs. Forcing one widget to
 * serve both would mean either a table of nameless times — useless — or a
 * calendar of names, which is the disclosure the design set out to avoid.
 */
import { useMemo } from "react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { cn } from "@gately/ui";
import { StatusBadge } from "@gately/ui/status-badge";
import { formatVisitInstant, viewerTimeZone } from "@gately/validators";

import type { ExpectedGuestRow } from "~/lib/expected-guests";
import type { DayBucket, StationRange } from "~/lib/station/expected-load";
import { TodayBookingsPanel } from "~/components/booking/TodayBookingsPanel";
import { StationChrome } from "~/components/station/station-chrome";
import { StationEyebrow } from "~/components/station/station-screen";
import { ConfirmArrivalButton } from "~/components/visitors/confirm-arrival-button";
import { VisitorBadges } from "~/components/visitors/visitor-badges";
import {
  RECURRING_GROUP_LABEL,
  upcomingExpectedGuests,
} from "~/lib/expected-guests";
import { orpc } from "~/lib/orpc";
import {
  arrivalInstant,
  barScale,
  bucketByDay,
  bucketDayOfMonth,
  expandRecurring,
  expectedHeadcount,
  monthGrid,
  rangeBounds,
  todaysExpectedRows,
} from "~/lib/station/expected-load";
import { useOrgTimeZone } from "~/lib/use-me";
import { usePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

// Resolved once per module, not per render. A kiosk's own zone is normally
// the venue's — this stays defensive rather than assumed, so a misconfigured
// or borrowed device still shows the discrepancy instead of hiding it.
const VIEWER = viewerTimeZone();

const RANGES: { value: StationRange; label: string }[] = [
  { value: "jour", label: "Jour" },
  { value: "semaine", label: "Semaine" },
  { value: "mois", label: "Mois" },
];

export const Route = createFileRoute("/poste/attendus")({
  // Kiosks get reloaded. Keeping the range in the URL means a reload does not
  // silently drop the gardien back onto today.
  validateSearch: z.object({
    range: z.enum(["jour", "semaine", "mois"]).default("jour"),
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(orpc.shift.current.queryOptions()),
      context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
    ]);
  },
  component: ExpectedPage,
});

function ExpectedPage() {
  const { range } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { hasAmenities } = usePermissions();

  return (
    <StationChrome>
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-2xl font-bold tracking-tight">
            Attendus
          </h1>
          <div
            role="tablist"
            aria-label="Période"
            className="flex gap-1 rounded-xl border border-white/10 bg-white/[0.04] p-1"
          >
            {RANGES.map((r) => (
              <button
                key={r.value}
                role="tab"
                type="button"
                aria-selected={range === r.value}
                onClick={() => void navigate({ search: { range: r.value } })}
                className={cn(
                  "h-11 min-w-24 rounded-lg px-4 text-[0.9375rem] font-medium transition-colors",
                  range === r.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {range === "jour" ? <DayView /> : <LoadView range={range} />}
        </div>

        {range === "jour" && hasAmenities ? <TodayBookingsPanel /> : null}
      </div>
    </StationChrome>
  );
}

function DayView() {
  const terms = useTerms();
  const timeZone = useOrgTimeZone();
  const { data: invites } = useSuspenseQuery({
    ...orpc.expected.today.queryOptions(),
    // Passes get created through the day, so today's list goes stale in a way
    // the week's shape does not.
    refetchInterval: 60_000,
  });

  const { groups, total, dayStart } = useMemo(() => {
    const now = new Date();
    // `expected.today()` already filters to today's résidence-local day, so
    // this is a second line rather than the only one — a stale query cache can
    // outlive midnight, and the day judgement must never come from the
    // browser's zone.
    const rows = todaysExpectedRows(
      upcomingExpectedGuests(invites, timeZone, now, terms.orgLabel, "masked"),
      now,
      timeZone,
    );

    // Every row here is already known to belong to today, so the dated group is
    // labelled outright. `groupExpectedGuestsByDay` would label it through
    // `dayLabel`, which resolves in the BROWSER's zone — and would additionally
    // split a still-valid multi-day pass that started last week into its own
    // past-dated group.
    const dated = rows.filter((r) => r.type !== "recurring");
    const recurring = rows.filter((r) => r.type === "recurring");
    const groups = [
      ...(dated.length > 0 ? [{ label: "Aujourd'hui", rows: dated }] : []),
      ...(recurring.length > 0
        ? [{ label: RECURRING_GROUP_LABEL, rows: recurring }]
        : []),
    ];

    return {
      groups,
      // People, not passes — the same unit the week and month views count in.
      total: expectedHeadcount(rows),
      dayStart: rangeBounds("jour", now, timeZone).from.getTime(),
    };
  }, [invites, terms.orgLabel, timeZone]);

  if (total === 0) {
    return <Empty>Aucune arrivée prévue aujourd&apos;hui.</Empty>;
  }

  return (
    <>
      <StationEyebrow>
        Arrivées prévues · jusqu&apos;à {total} personne
        {total === 1 ? "" : "s"}
      </StationEyebrow>
      <div className="mt-4 space-y-6">
        {groups.map((group) => (
          <section key={group.label}>
            <StationEyebrow className="mb-2">{group.label}</StationEyebrow>
            <div className="divide-y divide-white/10">
              {group.rows.map((row) => (
                <ExpectedRow key={row.guestId} row={row} dayStart={dayStart} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function ExpectedRow({
  row,
  dayStart,
}: {
  row: ExpectedGuestRow;
  dayStart: number;
}) {
  // A pass whose validity merely SPANS today (a stay that began on Monday, an
  // overnight window that opened last night) has no arrival time today, and
  // printing the day it did start next to a name would read as "expected at
  // 22:00" to a gardien scanning the column. A recurring row's validFrom is a
  // different animal — its date is the pattern's start, months in the past —
  // so `arrivalInstant` takes both meanings into account.
  const timeZone = useOrgTimeZone();
  const at = arrivalInstant(row, dayStart);
  const arrival = at
    ? formatVisitInstant(at, timeZone, { viewerTimeZone: VIEWER })
    : null;
  return (
    <div className="flex items-center gap-4 py-3.5">
      <span
        className="text-muted-foreground shrink-0 font-mono text-sm"
        title={arrival?.secondary ?? undefined}
      >
        {arrival?.primary ?? "—"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-base font-semibold">{row.guestName}</p>
          <VisitorBadges visitor={row.visitor} />
          {row.status === "incomplete" ? (
            <StatusBadge tone="warning">Infos non complétées</StatusBadge>
          ) : null}
        </div>
        <p className="text-muted-foreground truncate text-[0.8125rem]">
          {row.unitLabel}
        </p>
      </div>
      {row.maxHeadcount && row.maxHeadcount > 1 ? (
        <span className="text-muted-foreground shrink-0 text-sm">
          {row.maxHeadcount} pers.
        </span>
      ) : null}
      <div className="shrink-0">
        <ConfirmArrivalButton row={row} />
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground py-12 text-center text-base">
      {children}
    </p>
  );
}

/**
 * The anonymous half. No names, no units — the payload has none to render.
 * What the gardien gets is the shape of the week: where the rush is.
 */
function LoadView({ range }: { range: StationRange }) {
  const timeZone = useOrgTimeZone();
  const now = useMemo(() => new Date(), []);
  const bounds = useMemo(
    () => rangeBounds(range, now, timeZone),
    [range, now, timeZone],
  );

  const load = useQuery({
    ...orpc.expected.load.queryOptions({
      input: { from: bounds.from, to: bounds.to },
    }),
    // A planning view. Refetching on range change is enough.
    staleTime: 5 * 60_000,
  });

  const buckets = useMemo(() => {
    if (!load.data) return [];
    const occurrences = [
      ...load.data.dated.map((d) => ({
        at: new Date(d.at),
        headcount: d.headcount,
      })),
      ...expandRecurring(load.data.recurring, bounds.from, bounds.to, timeZone),
    ];
    return bucketByDay(occurrences, bounds, now, timeZone);
  }, [load.data, bounds, now, timeZone]);

  const total = buckets.reduce((sum, b) => sum + b.count, 0);

  if (load.isPending) return <Empty>Chargement…</Empty>;
  if (load.isError) {
    return <Empty>Impossible de charger les arrivées prévues.</Empty>;
  }

  return (
    <>
      {/* "jusqu'à", not a flat count: every occurrence is summed at its
          authorised `maxHeadcount`, which is a CEILING (the scanner and the
          mobile app the same gardien uses both say "jusqu'à N personnes").
          Summing the ceiling is the right number to staff a desk against, but
          it is not a prediction, and saying so plainly costs nothing. */}
      <StationEyebrow>
        {rangeLabel(bounds, timeZone)} · jusqu&apos;à {total} personne
        {total === 1 ? "" : "s"}
      </StationEyebrow>
      {total === 0 ? (
        <Empty>
          Aucune arrivée prévue{" "}
          {range === "semaine" ? "cette semaine" : "ce mois"}.
        </Empty>
      ) : range === "semaine" ? (
        <WeekBars buckets={buckets} />
      ) : (
        <MonthGrid buckets={buckets} />
      )}
    </>
  );
}

/**
 * "24 – 30 août" / "août 2026".
 *
 * Every field read here goes through an explicit `timeZone` rather than
 * `getDate()`/the runtime's default zone — `bounds.from`/`last` are instants,
 * and reading their calendar day without pinning the zone would answer in the
 * *kiosk's* zone instead of the organization's, silently mislabeling every
 * cell on a misconfigured machine.
 */
function rangeLabel(
  bounds: { from: Date; to: Date },
  timeZone: string,
): string {
  const last = new Date(bounds.to.getTime() - 1);
  const month = (d: Date, style: "long" | "short") =>
    d.toLocaleDateString("fr-FR", {
      month: style,
      timeZone,
    });
  const day = (d: Date) =>
    d.toLocaleDateString("fr-FR", {
      day: "numeric",
      timeZone,
    });
  const sameMonth = month(bounds.from, "long") === month(last, "long");
  if (!sameMonth) {
    return `${day(bounds.from)} ${month(bounds.from, "short")} – ${day(last)} ${month(last, "short")}`;
  }
  return `${day(bounds.from)} – ${day(last)} ${month(last, "long")}`;
}

const DAY_INITIALS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function WeekBars({ buckets }: { buckets: DayBucket[] }) {
  const scale = barScale(buckets);
  return (
    <div className="mt-5 grid grid-cols-7 gap-2">
      {buckets.map((b, i) => (
        <div
          key={b.key}
          className={cn(
            "flex flex-col items-center gap-2 rounded-xl border px-1 py-3",
            b.isToday
              ? "border-primary/50 bg-primary/8"
              : "border-white/10 bg-white/[0.03]",
          )}
        >
          <span className="text-muted-foreground text-xs font-medium">
            {DAY_INITIALS[i]}
          </span>
          <span className="font-mono text-sm text-neutral-300">
            {bucketDayOfMonth(b)}
          </span>
          <span className="font-display text-2xl font-bold">{b.count}</span>
          {/* The bar is decoration on top of the number, not a substitute for
              it — a count read at arm's length must not depend on comparing
              bar heights. */}
          <div
            aria-hidden="true"
            className="flex h-16 w-3 items-end overflow-hidden rounded-full bg-white/10"
          >
            <div
              className="bg-primary w-full rounded-full"
              style={{ height: `${Math.round((scale[i] ?? 0) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function MonthGrid({ buckets }: { buckets: DayBucket[] }) {
  const timeZone = useOrgTimeZone();
  const rows = monthGrid(buckets, timeZone);
  return (
    <div className="mt-5">
      <div className="mb-2 grid grid-cols-7 gap-2">
        {DAY_INITIALS.map((d) => (
          <span
            key={d}
            className="text-muted-foreground text-center text-xs font-medium"
          >
            {d}
          </span>
        ))}
      </div>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="grid grid-cols-7 gap-2">
            {row.map((cell, j) =>
              cell === null ? (
                <div key={`blank-${j}`} />
              ) : (
                <div
                  key={cell.key}
                  className={cn(
                    "flex h-16 flex-col items-center justify-center rounded-xl border",
                    cell.isToday
                      ? "border-primary/50 bg-primary/8"
                      : cell.count > 0
                        ? "border-white/10 bg-white/[0.06]"
                        : "border-white/5 bg-transparent",
                  )}
                >
                  <span className="text-muted-foreground font-mono text-[0.6875rem]">
                    {bucketDayOfMonth(cell)}
                  </span>
                  <span
                    className={cn(
                      "font-display text-lg font-bold",
                      cell.count === 0 && "text-muted-foreground/40",
                    )}
                  >
                    {cell.count}
                  </span>
                </div>
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
